/**
 * Region scope for the live-results shelf — pure display logic.
 *
 * Retrieval is COUNTRY-wide by design (see subregion-boost.ts: a per-subregion
 * slice would starve the IDF estimator), so "respecting the shopper's province"
 * is a presentation concern: say which scope the picks belong to, and put the
 * shopper's own region first when they asked for region-first ordering. Nothing
 * here filters — a nationwide page is never hidden, because for ~99.97% of
 * pages nationwide is all that exists and hiding them would empty the shelf.
 *
 * Scope resolution: the SERVER resolves the stored preference spelling to a
 * canonical admin-1 code with the same registry lookup the ordering boost uses
 * (searchPagesWithStatus returns it), so shelf and boost can never disagree.
 * The client-side fallback below exists only for shelves rendered WITHOUT a
 * fresh retrieval (a restored transcript snapshot): it accepts plain stored
 * codes ('QC'), which is every spelling that can match a page today — hit tags
 * are canonical codes — and stays out of the 48KB registry.
 */

import type { RegionPriority } from '@/lib/subregion-pref';

/** The shopper's admin-1 scope, resolved enough for the shelf to render. */
export interface ShelfSubregionScope {
  /** Canonical admin-1 code ('QC') — compared verbatim to `hit.subregion`. */
  code: string;
  /** Localized display name ('Québec') in the shelf's language. */
  name: string;
  /** The shopper's stated ordering. Only 'subregion-first' regroups cards. */
  priority: RegionPriority;
}

/**
 * The canonical code to scope the shelf on, or null when there is nothing to
 * say (no preference, whole-country sentinel, or an unresolvable legacy
 * spelling with no server resolution to lean on).
 *
 * `serverCode` (from searchPagesWithStatus) wins; the fallback accepts only
 * short uppercase-able codes so a legacy full-name spelling ('Île-de-France')
 * degrades to "no scope" instead of a chip that can never match a hit.
 */
export function resolveShelfScopeCode(
  serverCode: string | null | undefined,
  prefCode: string | null | undefined,
): string | null {
  const fromServer = serverCode?.trim().toUpperCase();
  if (fromServer) return fromServer;
  const fallback = prefCode?.trim().toUpperCase();
  if (fallback && /^[A-Z0-9]{2,3}$/.test(fallback)) return fallback;
  return null;
}

/** Minimal card shape the partition needs — matches LiveShelfCard. */
export interface ScopedCardLike {
  hitId: string;
}

export interface ShelfScopePartition<T extends ScopedCardLike> {
  /** Cards to render, in display order. Same array reference when unchanged. */
  cards: T[];
  /** How many cards belong to the shopper's own sub-region. */
  regionalCount: number;
  /** Per-card regional flags, aligned with `cards` — drives the card badge. */
  regional: boolean[];
}

/**
 * PURE. Mark each card with whether its page is about the shopper's own
 * sub-region, and — only for a 'subregion-first' shopper — move those cards to
 * the front of the strip. The move is a stable partition: relative order
 * inside each group survives verbatim, so the pipeline's ranking still decides
 * everything except which group leads.
 *
 * Country-first shoppers keep the incoming order (that IS their stated
 * preference); their regional cards still get flagged, because "this one is
 * from your province" is information, not reordering.
 */
export function partitionShelfCardsByScope<T extends ScopedCardLike>(
  cards: T[],
  subregionOfHit: ReadonlyMap<string, string | undefined>,
  scope: ShelfSubregionScope | null | undefined,
): ShelfScopePartition<T> {
  if (!scope?.code || cards.length === 0) {
    return { cards, regionalCount: 0, regional: cards.map(() => false) };
  }

  const isRegional = cards.map((card) => subregionOfHit.get(card.hitId) === scope.code);
  const regionalCount = isRegional.filter(Boolean).length;

  const shouldRegroup =
    scope.priority === 'subregion-first' && regionalCount > 0 && regionalCount < cards.length;
  if (!shouldRegroup) {
    return { cards, regionalCount, regional: isRegional };
  }

  const leading: T[] = [];
  const trailing: T[] = [];
  cards.forEach((card, index) => (isRegional[index] ? leading : trailing).push(card));
  const reordered = [...leading, ...trailing];
  return {
    cards: reordered,
    regionalCount,
    regional: reordered.map((_, index) => index < regionalCount),
  };
}
